import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scratchpadAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { SUBJECTS, ORGAN_SYSTEMS, cn, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
    ArrowLeft, Plus, Trash2, Edit, Users, StickyNote, Copy, Check, Settings,
    Tag, MoreVertical, Eye, Pencil, UserMinus, RefreshCw, Wifi, WifiOff
} from 'lucide-react';
import Layout from '../components/Layout';

const CATEGORIES = ['All', 'General', ...SUBJECTS, ...ORGAN_SYSTEMS];

export default function ScratchPadView() {
    const { padId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [pad, setPad] = useState(null);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('All');
    const [showAddNote, setShowAddNote] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [showMembers, setShowMembers] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [members, setMembers] = useState([]);
    const [owner, setOwner] = useState(null);
    const [wsConnected, setWsConnected] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);
    const [noteForm, setNoteForm] = useState({ title: '', body: '', category: 'General' });
    const [saving, setSaving] = useState(false);
    const [settingsForm, setSettingsForm] = useState({ name: '', description: '', tags: [] });
    const wsRef = useRef(null);
    const reconnectRef = useRef(null);

    const fetchPad = useCallback(async () => {
        try {
            const res = await scratchpadAPI.get(padId);
            setPad(res.data);
        } catch (err) {
            toast.error('Failed to load scratch pad');
            navigate('/study-planner');
        }
    }, [padId, navigate]);

    const fetchNotes = useCallback(async () => {
        try {
            const res = await scratchpadAPI.getNotes(padId, category);
            setNotes(res.data.notes || []);
        } catch (err) {
            console.error('Failed to fetch notes:', err);
        }
    }, [padId, category]);

    const fetchMembers = async () => {
        try {
            const res = await scratchpadAPI.getMembers(padId);
            setMembers(res.data.members || []);
            setOwner(res.data.owner);
        } catch (err) {
            console.error('Failed to fetch members:', err);
        }
    };

    useEffect(() => {
        const loadAll = async () => {
            setLoading(true);
            await fetchPad();
            await fetchNotes();
            setLoading(false);
        };
        loadAll();
    }, [fetchPad, fetchNotes]);

    // WebSocket connection
    useEffect(() => {
        const token = localStorage.getItem('medprep_token');
        if (!token || !padId) return;
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/scratchpads/${padId}/ws?token=${token}`;

        const connect = () => {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                setWsConnected(true);
                if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
            };

            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'note_created') {
                        setNotes(prev => [msg.note, ...prev]);
                    } else if (msg.type === 'note_updated') {
                        setNotes(prev => prev.map(n => n.id === msg.note.id ? msg.note : n));
                    } else if (msg.type === 'note_deleted') {
                        setNotes(prev => prev.filter(n => n.id !== msg.note_id));
                    } else if (msg.type === 'member_joined') {
                        toast.info(`${msg.user_name} joined the scratch pad`);
                    } else if (msg.type === 'user_connected') {
                        toast.info(`${msg.user_name} is now online`, { duration: 2000 });
                    } else if (msg.type === 'user_disconnected') {
                        // silent
                    }
                } catch (e) { /* ignore parse errors */ }
            };

            ws.onclose = () => {
                setWsConnected(false);
                reconnectRef.current = setTimeout(connect, 3000);
            };
            ws.onerror = () => { ws.close(); };
        };

        connect();

        // Ping keepalive
        const pingInterval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000);

        return () => {
            clearInterval(pingInterval);
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
        };
    }, [padId]);

    // Refetch notes when category changes
    useEffect(() => { if (!loading) fetchNotes(); }, [category]);

    const canEdit = pad?.role === 'owner' || pad?.role === 'edit';
    const isOwner = pad?.role === 'owner';

    const handleSaveNote = async () => {
        if (!noteForm.title.trim()) { toast.error('Title is required'); return; }
        setSaving(true);
        try {
            if (editingNote) {
                await scratchpadAPI.updateNote(padId, editingNote.id, noteForm);
                toast.success('Note updated');
            } else {
                await scratchpadAPI.createNote(padId, noteForm);
                toast.success('Note added');
            }
            setShowAddNote(false);
            setEditingNote(null);
            setNoteForm({ title: '', body: '', category: 'General' });
            fetchNotes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await scratchpadAPI.deleteNote(padId, noteId);
            toast.success('Note deleted');
            fetchNotes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete note');
        }
    };

    const handleEditNote = (note) => {
        setEditingNote(note);
        setNoteForm({ title: note.title, body: note.body, category: note.category });
        setShowAddNote(true);
    };

    const copyShareCode = () => {
        if (pad?.share_code) {
            navigator.clipboard.writeText(pad.share_code);
            setCodeCopied(true);
            toast.success('Code copied!');
            setTimeout(() => setCodeCopied(false), 2000);
        }
    };

    const handleUpdatePermission = async (userId, perm) => {
        try {
            await scratchpadAPI.updateMember(padId, userId, perm);
            toast.success('Permission updated');
            fetchMembers();
        } catch (err) {
            toast.error('Failed to update permission');
        }
    };

    const handleRemoveMember = async (userId) => {
        try {
            await scratchpadAPI.removeMember(padId, userId);
            toast.success('Member removed');
            fetchMembers();
        } catch (err) {
            toast.error('Failed to remove member');
        }
    };

    const handleSaveSettings = async () => {
        try {
            await scratchpadAPI.update(padId, settingsForm);
            toast.success('Settings saved');
            setShowSettings(false);
            fetchPad();
        } catch (err) {
            toast.error('Failed to save settings');
        }
    };

    const toggleSettingsTag = (tag) => {
        setSettingsForm(prev => ({
            ...prev,
            tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
        }));
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-12 w-full" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
                    </div>
                </div>
            </Layout>
        );
    }

    if (!pad) return null;

    return (
        <Layout>
            <div className="space-y-5 animate-fade-in" data-testid="scratchpad-view-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/study-planner')} className="mt-1 flex-shrink-0" data-testid="back-btn">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                                    {pad.name}
                                </h1>
                                <div className="flex items-center gap-1">
                                    {wsConnected ? (
                                        <Wifi className="h-4 w-4 text-emerald-500" />
                                    ) : (
                                        <WifiOff className="h-4 w-4 text-slate-300" />
                                    )}
                                </div>
                            </div>
                            {pad.description && <p className="text-sm text-slate-500">{pad.description}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Share code */}
                        <Button
                            variant="outline" size="sm"
                            onClick={copyShareCode}
                            className="font-mono tracking-wider"
                            data-testid="copy-code-btn"
                        >
                            {codeCopied ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                            {pad.share_code}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { fetchMembers(); setShowMembers(true); }} data-testid="members-btn">
                            <Users className="h-4 w-4 mr-1.5" /> Members
                        </Button>
                        {isOwner && (
                            <Button variant="outline" size="sm" onClick={() => {
                                setSettingsForm({ name: pad.name, description: pad.description || '', tags: pad.tags || [] });
                                setShowSettings(true);
                            }} data-testid="settings-btn">
                                <Settings className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Category filter + Add note */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 max-w-xs">
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="category-filter">
                                <Tag className="h-4 w-4 mr-2 text-slate-400" />
                                <SelectValue placeholder="Filter by category" />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map(c => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <span className="text-sm text-slate-400">{notes.length} notes</span>
                    {canEdit && (
                        <Button size="sm" onClick={() => {
                            setEditingNote(null);
                            setNoteForm({ title: '', body: '', category: category !== 'All' ? category : 'General' });
                            setShowAddNote(true);
                        }} data-testid="add-note-btn">
                            <Plus className="h-4 w-4 mr-1.5" /> Add Note
                        </Button>
                    )}
                </div>

                {/* Notes grid */}
                {notes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {notes.map(note => (
                            <Card key={note.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`note-card-${note.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="min-w-0 flex-1">
                                            <Badge variant="outline" className="text-xs border-slate-200 text-slate-500 mb-1.5">
                                                {note.category}
                                            </Badge>
                                            <h4 className="font-semibold text-slate-800 text-sm leading-tight">
                                                {note.title}
                                            </h4>
                                        </div>
                                        {(canEdit || note.created_by === user?.id) && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" data-testid={`note-menu-${note.id}`}>
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {canEdit && (
                                                        <DropdownMenuItem onClick={() => handleEditNote(note)} className="cursor-pointer">
                                                            <Edit className="h-4 w-4 mr-2" /> Edit
                                                        </DropdownMenuItem>
                                                    )}
                                                    {(isOwner || note.created_by === user?.id) && (
                                                        <DropdownMenuItem onClick={() => handleDeleteNote(note.id)} className="cursor-pointer text-red-600">
                                                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                                        {note.body}
                                    </p>
                                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                                        <span>{note.created_by_name}</span>
                                        <span>{formatDate(note.updated_at)}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="border-dashed border-2 border-slate-200">
                        <CardContent className="p-12 text-center">
                            <StickyNote className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Notes Yet</h3>
                            <p className="text-slate-500 mb-4">
                                {canEdit ? 'Add your first note to get started' : 'No notes have been added yet'}
                            </p>
                            {canEdit && (
                                <Button onClick={() => { setEditingNote(null); setNoteForm({ title: '', body: '', category: 'General' }); setShowAddNote(true); }}>
                                    <Plus className="mr-2 h-4 w-4" /> Add Note
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Add/Edit Note Dialog */}
                <Dialog open={showAddNote} onOpenChange={(open) => { if (!open) { setShowAddNote(false); setEditingNote(null); } }}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle style={{ fontFamily: 'Manrope' }}>{editingNote ? 'Edit Note' : 'Add Note'}</DialogTitle>
                            <DialogDescription>Jot down a key concept, clue, or study tip</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="space-y-2">
                                <Label>Title</Label>
                                <Input
                                    placeholder="e.g., Diabetes presents with 3 P's"
                                    value={noteForm.title}
                                    onChange={e => setNoteForm({ ...noteForm, title: e.target.value })}
                                    data-testid="note-title-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Body</Label>
                                <Textarea
                                    placeholder="Polyuria, Polydipsia, Polyphagia..."
                                    value={noteForm.body}
                                    onChange={e => setNoteForm({ ...noteForm, body: e.target.value })}
                                    rows={5}
                                    data-testid="note-body-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Category</Label>
                                <Select value={noteForm.category} onValueChange={v => setNoteForm({ ...noteForm, category: v })}>
                                    <SelectTrigger data-testid="note-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="General">General</SelectItem>
                                        {SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                        {ORGAN_SYSTEMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setShowAddNote(false); setEditingNote(null); }}>Cancel</Button>
                            <Button onClick={handleSaveNote} disabled={saving} data-testid="save-note-btn">
                                {saving ? 'Saving...' : editingNote ? 'Update' : 'Add Note'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Members Dialog */}
                <Dialog open={showMembers} onOpenChange={setShowMembers}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle style={{ fontFamily: 'Manrope' }}>Members</DialogTitle>
                            <DialogDescription>People who have access to this scratch pad</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                            {/* Owner */}
                            {owner && (
                                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100">
                                    <div>
                                        <p className="text-sm font-medium text-slate-800">{owner.user_name}</p>
                                        <p className="text-xs text-slate-500">Owner</p>
                                    </div>
                                    <Badge className="bg-indigo-100 text-indigo-700 text-xs">Owner</Badge>
                                </div>
                            )}
                            {/* Members */}
                            {members.length > 0 ? members.map(m => (
                                <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200">
                                    <div>
                                        <p className="text-sm font-medium text-slate-800">{m.user_name}</p>
                                        <p className="text-xs text-slate-500">{m.user_email}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isOwner ? (
                                            <>
                                                <Select value={m.permission} onValueChange={v => handleUpdatePermission(m.user_id, v)}>
                                                    <SelectTrigger className="w-24 h-8 text-xs" data-testid={`perm-select-${m.user_id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="view"><Eye className="h-3 w-3 mr-1 inline" />View</SelectItem>
                                                        <SelectItem value="edit"><Pencil className="h-3 w-3 mr-1 inline" />Edit</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50"
                                                    onClick={() => handleRemoveMember(m.user_id)}
                                                    data-testid={`remove-member-${m.user_id}`}
                                                >
                                                    <UserMinus className="h-4 w-4" />
                                                </Button>
                                            </>
                                        ) : (
                                            <Badge variant="outline" className="text-xs">
                                                {m.permission === 'edit' ? 'Editor' : 'Viewer'}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            )) : (
                                <p className="text-sm text-slate-500 text-center py-4">No members yet. Share the code to invite people.</p>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Settings Dialog */}
                <Dialog open={showSettings} onOpenChange={setShowSettings}>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle style={{ fontFamily: 'Manrope' }}>Scratch Pad Settings</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="space-y-2">
                                <Label>Name</Label>
                                <Input value={settingsForm.name} onChange={e => setSettingsForm({ ...settingsForm, name: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Input value={settingsForm.description} onChange={e => setSettingsForm({ ...settingsForm, description: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Tags</Label>
                                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                    {[...SUBJECTS, ...ORGAN_SYSTEMS].map(tag => (
                                        <Badge
                                            key={tag}
                                            variant={settingsForm.tags.includes(tag) ? "default" : "outline"}
                                            className={cn("cursor-pointer text-xs transition-all", settingsForm.tags.includes(tag) && "bg-indigo-500")}
                                            onClick={() => toggleSettingsTag(tag)}
                                        >
                                            {tag}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
                            <Button onClick={handleSaveSettings} data-testid="save-settings-btn">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
